import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { environment } from 'environments/environment';

import * as _ from 'lodash';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/delay';
import 'rxjs/add/operator/catch';

import { User } from '../models';

const AUTH_STORAGE_KEY = 'auth';

@Injectable()
export class AuthService {

  private _userObs: ReplaySubject<User>;
  private _rightsObs: ReplaySubject<any>;

  private _storageLastLogin: Date | null = null;
  private _storageUser: User | null = null;
  private _storageAuthToken: string | null = null;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  public login (identifier: string, password: string): Observable<any> {
    return this.http.post<{ authToken: string }>(`${environment.API_URL}/ws/mw/login?json`, {
      identifier: identifier,
      password: password,
    })
    .map((data) => {
      this.storageLastLogin = new Date();
      this.storageAuthToken = data.authToken;
    })
    .delay(1000)
    .mergeMap(() => this.refreshUser());
  }

  public silentLogin (): Observable<User | null> {
    if (!this._userObs) {
      this._userObs = new ReplaySubject<User>(1);
    }
    try {
      const dataStr = localStorage.getItem(AUTH_STORAGE_KEY);
      const authObject = JSON.parse(dataStr);
      if (!authObject || !authObject.user) {
        this._userObs.next(null);
        return Observable.of(null);
      }
      else {
        this._storageLastLogin = new Date(authObject.lastLogin);
        this._storageUser = new User(authObject.user);
        this._storageAuthToken = authObject.authToken;
  
        this._userObs.next(this._storageUser);
        return Observable.of(this._storageUser);
      }
    }
    catch (err) {
      console.error('error parsing data from localstorage', err);
      return Observable.of(null);
    }
  }

  public getUser(force?: boolean): Observable<User> {
    if (this._userObs && !force){
      return this._userObs;
    }
    else {
      return this.refreshUser();
    }
  }

  public refreshUser(): Observable<User> {
    if (!this._userObs) {
      this._userObs = new ReplaySubject<User>(1);
    }
    return this.http.get(`${environment.API_URL}/ws/mw/me`)
      .catch(() => Observable.of(null))
      .map((userData) => {
        if(userData) userData = new User(userData);
        else userData = null;
        this.storageUser = userData;
        this._userObs.next(userData);
      })
      .mergeMap(() => this._userObs);
  }

  public logout () {
    this.http.get(`${environment.API_URL}/ws/mw/logout`)
      .subscribe(() => {
        this.clearAuth();
        this.router.navigate(['/login']);
      }, (err) => {
        this._userObs.next(null);
        throw err;
      });
  }

  public clearAuth () {
    this._userObs.next(null);
    this.storageUser = null;
  }

  public isLogged(): Observable<boolean> {
    return this.getUser()
      .map((user) => Boolean(user));
  }

  public getRights(force?: boolean): Observable<any> {
    if (this._rightsObs && !force){
      return this._rightsObs;
    }
    else {
      return this.refreshRights();
    }
  }

  public refreshRights(): Observable<any> {
    if (!this._rightsObs) {
      this._rightsObs = new ReplaySubject<any>(1);
    }
    return this.http.get(`${environment.API_URL}/ws/app/rights`)
      .map((rightsData) => {
        if (!rightsData) rightsData = null;
        this._rightsObs.next(rightsData);
      })
      .mergeMap(() => this._rightsObs);
  }

  // role can have & or | to separate multiple roles. & has priority.
  public userIs(roles: string): Observable<boolean> {
    if (!roles) return Observable.of(true);

    let rolesToCheck = this.parseRightsToCheck(roles);

    return this.getUser()
    .map((user) => {
      let userRoles = _.get(user, 'roles', []);
      return this.checkListRights(userRoles, rolesToCheck);
    });
  }

  // right can have & or | to separate multiple rights. & has priority.
  public userCan(rights: string): Observable<boolean> {

    let rightsToCheck = this.parseRightsToCheck(rights);

    return Observable.combineLatest(
      this.getUser(),
      this.getRights()
    )
    .map(([user, rightsConfig]) => {
      let userRoles = _.get(user, 'roles', []) as string[];

      let userRights = userRoles.reduce((rights, role) => {
        return _.uniq(rights.concat(_.get(rightsConfig, ['roles', role], [])));
      }, []);

      return this.checkListRights(userRights, rightsToCheck);
    })
  }

  // Create an array (for or) of array (for and)
  // ex: 'a|b&c' => [[a], [b, c]] => a or b and c
  private parseRightsToCheck (rights: string): string[][] {
      // Split or operands
    return rights.split('|')
      .map((right) => right.trim()).filter((right) => right.length)

    .map((right) => {
      // Split and operands
      return _.uniq(
        right.split('&')
          .map((right) => right.trim()).filter((right) => right.length)
      );
    });
  }

  private checkListRights (rights: string[], rightsToCheck: string[][]): boolean {
    return rights.length
      && rightsToCheck.some((rightsAnd) => {
        return _.intersection(rights, rightsAnd).length === rightsAnd.length;
      });
  }

  public get storageUser () {
    return this._storageUser;
  }

  public set storageUser (data) {
    this._storageUser = data;
    this.saveStorageObject();
  }

  public get storageLastLogin () {
    return this._storageLastLogin;
  }

  public set storageLastLogin (date) {
    this._storageLastLogin = date;
    this.saveStorageObject();
  }

  public get storageAuthToken () {
    return this._storageAuthToken;
  }

  public set storageAuthToken (token) {
    this._storageAuthToken = token;
    this.saveStorageObject();
  }

  private saveStorageObject () {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({
      user: this._storageUser,
      lastLogin: this._storageLastLogin,
      authToken: this._storageAuthToken,
    }));
  }
}



// WEBPACK FOOTER //
// ./src/app/auth/auth.service.ts